import { io, Socket } from 'socket.io-client'
import toast from 'react-hot-toast'

export interface NotificationData {
  type: string
  title: string
  message: string
  data: any
  timestamp: string
}

class SocketService {
  private socket: Socket | null = null
  private isConnected = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5

  connect(token: string) {
    if (this.socket?.connected) {
      console.log('🔌 Socket already connected')
      return
    }

    try {
      // this.socket = io(import.meta.env.VITE_SOCKET_URL || 'https://api.connecttechusa.com', {
      this.socket = io('https://api.connecttechusa.com', {
        auth: { token },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      })

      this.setupEventListeners()
      console.log('🔌 Socket connection initiated')
    } catch (error) {
      console.error('❌ Failed to connect socket:', error)
    }
  }

  private setupEventListeners() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket?.id)
      this.isConnected = true
      this.reconnectAttempts = 0
    })

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason)
      this.isConnected = false
    })

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error)
      this.reconnectAttempts++
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ Max reconnection attempts reached')
      }
    })

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts')
      this.isConnected = true
    })

    // Handle new notifications
    this.socket.on('notification:new', (notification: NotificationData) => {
      console.log('📢 Received new notification:', notification)
      this.showNotification(notification)
      // Emit custom event for notifications page to listen to
      window.dispatchEvent(new CustomEvent('notification:new', { detail: notification }))
    })

    // Handle post order updates
    this.socket.on('post_order:new', (data: any) => {
      console.log('📋 New post order:', data)
      toast.success(`New post order available: ${data.title}`, {
        duration: 4000,
        icon: '📋'
      })
      // Emit custom event for post orders page
      window.dispatchEvent(new CustomEvent('post_order:new', { detail: data }))
    })

    // Handle post order completion
    this.socket.on('post_order:completed', (data: any) => {
      console.log('✅ Post order completed:', data)
      toast.success(`Post order completed: ${data.title}`, {
        duration: 4000,
        icon: '✅'
      })
      // Emit custom event for post orders page
      window.dispatchEvent(new CustomEvent('post_order:completed', { detail: data }))
    })

    // Handle location updates
    this.socket.on('location:updated', (data: any) => {
      console.log('📍 Location updated:', data)
      toast.success(`Location ${data.name} has been updated`, {
        duration: 4000,
        icon: '📍'
      })
      // Emit custom event for locations page
      window.dispatchEvent(new CustomEvent('location:updated', { detail: data }))
    })

    // Handle organization updates
    this.socket.on('organization:updated', (data: any) => {
      console.log('🏢 Organization updated:', data)
      toast.success('Organization information has been updated', {
        duration: 4000,
        icon: '🏢'
      })
      // Emit custom event for organization management
      window.dispatchEvent(new CustomEvent('organization:updated', { detail: data }))
    })

    // Handle profile updates
    this.socket.on('profile:updated', (data: any) => {
      console.log('👤 Profile updated:', data)
      toast.success('Your profile has been updated', {
        duration: 4000,
        icon: '👤'
      })
      // Emit custom event for profile page
      window.dispatchEvent(new CustomEvent('profile:updated', { detail: data }))
    })

    // Handle check-in confirmation
    this.socket.on('checkin:confirmed', (data: any) => {
      console.log('📍 Check-in confirmed:', data)
      toast.success(`Check-in confirmed at ${data.locationName}`, {
        duration: 4000,
        icon: '📍'
      })
      // Emit custom event for check-in page
      window.dispatchEvent(new CustomEvent('checkin:confirmed', { detail: data }))
    })

    // Handle check-out confirmation
    this.socket.on('checkout:confirmed', (data: any) => {
      console.log('📍 Check-out confirmed:', data)
      toast.success(`Check-out confirmed from ${data.locationName}`, {
        duration: 4000,
        icon: '📍'
      })
      // Emit custom event for check-in page
      window.dispatchEvent(new CustomEvent('checkout:confirmed', { detail: data }))
    })

    // Handle admin-initiated check-out
    this.socket.on('admin:checkout:forced', (data: any) => {
      console.log('🚨 Admin forced check-out:', data)
      
      // Show alert to user that they were checked out by admin
      toast.error(`You have been checked out by an administrator from ${data.locationName}`, {
        duration: 8000,
        icon: '🚨',
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          border: 'none'
        }
      })
      
      // Emit custom event for check-in page to handle the forced checkout
      window.dispatchEvent(new CustomEvent('admin:checkout:forced', { detail: data }))
    })

    // Handle incident report confirmation
    this.socket.on('incident:confirmed', (data: any) => {
      console.log('🚨 Incident report confirmed:', data)
      toast.success(`Incident report submitted successfully`, {
        duration: 4000,
        icon: '🚨'
      })
      // Emit custom event for incident report page
      window.dispatchEvent(new CustomEvent('incident:confirmed', { detail: data }))
    })

    // Handle hourly report confirmation
    this.socket.on('hourly_report:confirmed', (data: any) => {
      console.log('📊 Hourly report confirmed:', data)
      toast.success(`Hourly report submitted successfully`, {
        duration: 4000,
        icon: '📊'
      })
      // Emit custom event for hourly report page
      window.dispatchEvent(new CustomEvent('hourly_report:confirmed', { detail: data }))
    })

    // Handle user removal from organization
    this.socket.on('user:removed_from_organization', (data: any) => {
      console.log('🚨 User removed from organization:', data)
      toast.error(`You have been removed from ${data.organizationName}`, {
        duration: 8000,
        icon: '🚨',
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          border: 'none'
        }
      })
      // Emit custom event for auth context to handle organization switching/logout
      window.dispatchEvent(new CustomEvent('user:removed_from_organization', { detail: data }))
    })
  }

  private showNotification(notification: NotificationData) {
    // Create a beautiful toast notification
    const toastOptions = {
      duration: 6000,
      position: 'bottom-right' as const,
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '12px',
        padding: '16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        border: 'none'
      }
    }

    // Custom notification based on type
    switch (notification.type) {
      case 'notification_new':
        toast.success(
          `${notification.title}\n${notification.message}`,
          {
            ...toastOptions,
            icon: '📢'
          }
        )
        break

      case 'post_order_new':
        toast.success(
          `${notification.title}\n${notification.message}`,
          {
            ...toastOptions,
            icon: '📋',
            style: {
              ...toastOptions.style,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            }
          }
        )
        break

      case 'post_order_completed':
        toast.success(
          `${notification.title}\n${notification.message}`,
          {
            ...toastOptions,
            icon: '✅',
            style: {
              ...toastOptions.style,
              background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
            }
          }
        )
        break

      case 'checkin_confirmed':
        toast.success(
          `${notification.title}\n${notification.message}`,
          {
            ...toastOptions,
            icon: '📍',
            style: {
              ...toastOptions.style,
              background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
            }
          }
        )
        break

      case 'incident_confirmed':
        toast.success(
          `${notification.title}\n${notification.message}`,
          {
            ...toastOptions,
            icon: '🚨',
            style: {
              ...toastOptions.style,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
            }
          }
        )
        break

      case 'hourly_report_confirmed':
        toast.success(
          `${notification.title}\n${notification.message}`,
          {
            ...toastOptions,
            icon: '📊',
            style: {
              ...toastOptions.style,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
            }
          }
        )
        break

      default:
        toast.success(
          `${notification.title}\n${notification.message}`,
          toastOptions
        )
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      console.log('🔌 Socket disconnected')
    }
  }

  isConnectedStatus() {
    return this.isConnected
  }

  // Emit events to server
  emit(event: string, data: any) {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    } else {
      console.warn('⚠️ Socket not connected, cannot emit event:', event)
    }
  }
}

export const socketService = new SocketService() 